/**
 * ЗВУК НЕПОГОДЫ: пока над полем идет непогода (вьюга над зимним полем — см.
 * utils/effects/biome/snowSquall), звучит ее звук — вой ветра.
 *
 * Звук и картинка идут только вместе:
 *  - звук — отложенный (configs/deferredSounds): качается в фоне и только на
 *    тех подложках, где он нужен. Пока он не загружен, непогода на поле НЕ
 *    НАЧИНАЕТСЯ (checkIfReady): заряд без воя владельцу игры не нужен;
 *  - слышимость звука идет за силой непогоды: картинка плавно нарастает —
 *    плавно нарастает и вой, стихает — стихает и он. Скачком слышимость не
 *    меняется, даже если картинку убрали разом (поле перестроили).
 *
 * Там, где звука нет вовсе (демо-партия меню с выключенным звуком, сцена без
 * звуковой подсистемы), ждать нечего: непогода идет молча.
 *
 * Время — шаг времени кадра от FieldAmbience, тот же, что двигает картинку: в
 * скрытой вкладке стоит и то, и другое.
 *
 * Звук — украшение украшения: сбой в нем не должен задеть антураж, поэтому
 * наружу отсюда ничего не выходит.
 */

// Звучащим звуком можно управлять, если SoundManager отдал его целиком
const SOUND_CONTROLS = ['checkIfPlaying', 'setLevel', 'stop'];

export default class FieldWeatherSound {
  /**
   * @param {Phaser.Scene} scene — сцена с soundManager
   * @param {Object} options
   * @param {string} options.soundId — имя отложенного звука
   * @param {number} [options.fadeMs] — быстрее, чем за столько, слышимость
   *   не проходит от тишины до полной (и обратно)
   */
  constructor(scene, {soundId, fadeMs = 900} = {}) {
    this.scene = scene;
    this.soundId = soundId;
    this.fadeMs = fadeMs;
    // Звучащий звук от SoundManager.playDeferredSound и его слышимость 0..1
    this.sound = null;
    this.level = 0;
  }

  /**
   * Можно ли непогоде начаться: ее звук загружен — или звука в этой сцене нет вовсе
   * @returns {boolean}
   */
  checkIfReady() {
    try {
      const soundManager = this.scene?.soundManager;
      if (typeof soundManager?.checkIfDeferredSoundLoaded !== 'function') {
        return true;
      }
      if (soundManager.checkIfSoundOff?.()) {
        return true;
      }
      return Boolean(soundManager.checkIfDeferredSoundLoaded(this.soundId));
    } catch (error) {
      console.warn('[FieldWeatherSound] Не удалось узнать, загружен ли звук непогоды', error);
      return false;
    }
  }

  /**
   * Шаг времени: слышимость идет за силой непогоды
   * @param {number} stepMs — шаг времени кадра (тот же, что двигает картинку)
   * @param {number} strength — сила непогоды сейчас: 0 — ее нет, 1 — в разгаре
   */
  update(stepMs, strength) {
    try {
      const target = Number.isFinite(strength) ? Math.min(1, Math.max(0, strength)) : 0;
      if (!this.sound && target > 0) {
        this.start();
      }
      if (!this.sound) {
        return;
      }
      // Доиграл сам или оборван извне — следить больше не за чем
      if (!this.sound.checkIfPlaying()) {
        this.sound = null;
        this.level = 0;
        return;
      }
      const maxStep = stepMs > 0 ? stepMs / this.fadeMs : 0;
      this.level += Math.min(maxStep, Math.max(-maxStep, target - this.level));
      if (target <= 0 && this.level <= 0) {
        this.stop();
        return;
      }
      this.sound.setLevel(this.level);
    } catch (error) {
      console.warn('[FieldWeatherSound] Не удалось сыграть звук непогоды', error);
      this.stop();
    }
  }

  /** Запустить звук неслышным: дальше его слышимость поднимает update */
  start() {
    const sound = this.scene?.soundManager?.playDeferredSound?.(this.soundId, {level: 0});
    // Звук не сыгран (выключен, браузер еще не дал звучать) или им нельзя
    // управлять — попробуем на следующем шаге
    if (!sound || SOUND_CONTROLS.some((control) => typeof sound[control] !== 'function')) {
      return;
    }
    this.sound = sound;
    this.level = 0;
  }

  /** Оборвать звук разом: сцена уходит или картинки больше нет и шагов времени не будет */
  stop() {
    const sound = this.sound;
    this.sound = null;
    this.level = 0;
    try {
      sound?.stop();
    } catch (error) {
      console.warn('[FieldWeatherSound] Не удалось остановить звук непогоды', error);
    }
  }
}
